import { devtools, persist } from 'zustand/middleware';
import { createWithEqualityFn as create } from 'zustand/traditional';
import { FloraVersion } from '../../containers/flora/flora.type';
import { createSafeJSONStorage } from '../../helpers/storage/persist-storage';
import { isLoggingEnabled, logger } from '../logger/logger';
import type { State } from './global-store.type';

const useGlobalStore = create<State>()(
  devtools(
    persist(
      logger((_) => ({
        initialized: false,
        organization: null,
        organizations: [],
        portfolio: null,
        portfolios: [],
        teams: [],
        useAI: false,
        useAIResolved: false,
        useAIResolvedOrganizationId: null,
        floraIntegrationStatuses: {
          slack: false,
          microsoft_teams: false,
          webex: false,
        },
        floraVersionsAvailable: [FloraVersion.LANGCHAIN],
        isSidebarOpen: true,
        superuserMode: {
          enabled: false,
          forceRecalculation: false,
          showModelSelection: false,
        },
        floraVersion: FloraVersion.LANGCHAIN,
        floraModel: '',
        floraEffort: null,
        floraModelsAvailable: [],
        floraChatApprovalModes: {},
        floraDraftApprovalMode: null,
      })),
      {
        name: 'global-storage',
        storage: createSafeJSONStorage(() => localStorage),
        partialize: (state) =>
          Object.fromEntries(
            Object.entries(state).filter(
              ([key]) =>
                ![
                  'initialized',
                  'useAI',
                  'useAIResolved',
                  'useAIResolvedOrganizationId',
                  'floraIntegrationStatuses',
                  'floraVersionsAvailable',
                  'floraModelsAvailable',
                  'floraChatApprovalModes',
                  'floraDraftApprovalMode',
                ].includes(key),
            ),
          ),
        version: 2,
        migrate: (persistedState, version) => {
          const state = (persistedState ?? {}) as Partial<State> & { floraApprovalMode?: unknown };
          if (version < 1) {
            // v1: Clear portfolio to force re-selection using new default portfolio logic (is_default)
            state.portfolio = null;
          }
          // v2: The composer Ask/Auto choice became per chat; drop the old global
          // override so it cannot leak into every chat.
          delete state.floraApprovalMode;
          return state as State;
        },
      },
    ),
    {
      name: 'Bloomfilter - Global Store',
      enabled: isLoggingEnabled,
    },
  ),
);

export { useGlobalStore };
